// Factory function pattern to avoid Webpack TDZ issues with class inheritance
// This replaces BaseApiService class with a factory function to prevent initialization order problems

import type { ApiPayload, QueryPayload, RequestOptions, ProcessResponseOptions } from "./types";
import { ContentType } from "./ContentType";
import { getBaseUrl, invalidateBaseUrl } from "./helpers/configHelpers";
import { buildHeaders } from "./helpers/headerHelpers";
import { buildBody, appendJsonSearchParams, onError } from "./helpers/requestHelpers";
import { processResponse } from "./helpers/responseHelpers";
import { createPostFormDataRequest, createPutFormDataRequest } from "./BaseApiServiceFactory/helpers/formDataRequestHelpers";
import {
	getRequest,
	postRequest,
	putRequest,
	patchRequest,
	deleteRequest,
} from "./BaseApiServiceFactory/helpers/httpMethodsHelpers";

export { invalidateBaseUrl };

export interface IBaseApiService {
	getBaseUrl(): string;
	buildHeaders(contentType: ContentType): Headers;
	buildBody(body: ApiPayload): string;
	appendJsonSearchParams(url: URL, params: QueryPayload): void;
	getRequest<T>(url: URL, options?: RequestOptions): Promise<T>;
	postRequest<T>(url: URL, body?: ApiPayload, _token?: string): Promise<T>;
	postRequestFormData<T>(url: URL, body: FormData): Promise<T>;
	putRequest<T>(url: URL, body?: ApiPayload, _token?: string): Promise<T>;
	patchRequest<T>(url: URL, body?: ApiPayload): Promise<T>;
	deleteRequest<T>(url: URL, body?: ApiPayload, _token?: string): Promise<T>;
	putFormDataRequest<T>(url: URL, body: FormData, _token?: string): Promise<T>;
	processResponse<T>(
		response: Response,
		request: () => Promise<Response>,
		options?: ProcessResponseOptions,
	): Promise<T>;
	onError(error: unknown): void;
}

export function createBaseApiService(): IBaseApiService {
	return {
		getBaseUrl,
		buildHeaders,
		buildBody,
		appendJsonSearchParams,
		getRequest,
		postRequest,
		postRequestFormData: createPostFormDataRequest,
		putRequest,
		patchRequest,
		deleteRequest,
		putFormDataRequest: createPutFormDataRequest,
		async processResponse<T>(
			response: Response,
			request: () => Promise<Response>,
			options: ProcessResponseOptions = {},
		): Promise<T> {
			return processResponse<T>(response, request, options);
		},
		onError,
	};
}
